import Avatar from 'components/common/Avatar';
import React, { useContext, useState } from 'react';

import { Comment as CommunityComment } from 'types/comment';
import CommentReplyList from './CommentReplyList';
import styles from './Comment.module.css';
import ToggleReplies from './ToggleReplies';
import CommentMenuButton from './CommentMenuButton';
import CommentAuthor from './CommentAuthor';
import AddComment from './AddComment';
import { UserContext } from 'contexts/user';
import { CommunityShotItem } from 'types/community';
import CommentContents from './CommentContents';
import {
  commentsAddReply,
  commentsCancelReply,
  commentsHideReplies,
  commentsShowReplies,
} from 'lib/analytics';

type Props = {
  comment: CommunityComment;
  repliesEnabled: Boolean;
  shotItem: CommunityShotItem;
  targetCommentId?: number;
};

function Comment({
  comment,
  repliesEnabled,
  shotItem,
  targetCommentId,
}: Props) {
  if (typeof window === 'undefined') return null;

  const { user } = useContext(UserContext);

  const loginHref = `/login/?redirect=${encodeURIComponent(
    window.location.pathname
  )}`;
  const isSignedIn = user?.signedIn;
  const isOwner = user?.ownerId === comment.owner.ownerId;
  const [addReply, setAddReply] = useState<Boolean>(false);

  let defaultShowReplies = false;
  if (targetCommentId && comment.replies) {
    for (const reply of comment.replies) {
      if (reply.commentId === targetCommentId) {
        defaultShowReplies = true;
        break;
      }
    }
  }
  const [showReplies, setShowReplies] = useState<Boolean>(defaultShowReplies);
  function updateShowReplies(value: Boolean) {
    setShowReplies(value);
    if (value) {
      commentsShowReplies(
        comment.commentId,
        shotItem.communityPostId,
        shotItem.title,
        shotItem.fileId
      );
    } else {
      commentsHideReplies(
        comment.commentId,
        shotItem.communityPostId,
        shotItem.title,
        shotItem.fileId
      );
    }
  }

  const commentClass =
    targetCommentId === comment.commentId
      ? styles.TargetComment + ' ' + styles.Comment
      : styles.Comment;

  return (
    <div id={`comment${comment.commentId}`}>
      <div className={commentClass}>
        <div className={styles.CommentFirstColumn}>
          <Avatar data={comment.owner} />
        </div>
        <div className={styles.CommentMainColumn}>
          <CommentAuthor author={comment.owner} />
          <CommentContents comment={comment} />

          {repliesEnabled && (
            <div className={styles.CommentActions}>
              {comment.replies.length > 0 && (
                <ToggleReplies
                  replyCount={comment.replies.length}
                  setShowReplies={updateShowReplies}
                  showReplies={showReplies}
                />
              )}
              {addReply && (
                <button
                  className={styles.CommentAction}
                  onClick={() => {
                    commentsCancelReply(
                      comment.commentId,
                      shotItem.communityPostId,
                      shotItem.title,
                      shotItem.fileId
                    );
                    setAddReply(false);
                  }}
                >
                  Cancel
                </button>
              )}
              {!addReply && isSignedIn && (
                <button
                  className={styles.CommentAction}
                  onClick={() => {
                    // always show replies when you click the reply button. looks weird otherwise.
                    setShowReplies(true);
                    setAddReply(true);
                    commentsAddReply(
                      comment.commentId,
                      shotItem.communityPostId,
                      shotItem.title,
                      shotItem.fileId
                    );
                  }}
                >
                  Reply
                </button>
              )}
              {!addReply && !isSignedIn && (
                <a className={styles.CommentLogin} href={loginHref}>
                  Log in to reply
                </a>
              )}
            </div>
          )}
        </div>
        <div className={styles.CommentLastColumn}>
          {isSignedIn && (
            <CommentMenuButton isOwner={isOwner} comment={comment} />
          )}
        </div>
      </div>
      <div className={styles.CommentReplyList}>
        {addReply && (
          <AddComment
            shotItem={shotItem}
            replyToCommentId={comment.commentId}
            submitCallback={() => setAddReply(false)}
          />
        )}
        {comment.replies.length > 0 && showReplies && (
          <CommentReplyList
            shotItem={shotItem}
            replies={comment.replies}
            targetCommentId={targetCommentId}
          />
        )}
      </div>
    </div>
  );
}

export default Comment;
